import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  Address,
  Coordinates,
  IndoorUnit,
  Locale,
  MvEnv,
  Platform,
  ProductInstanceStatus,
  TokenType,
} from '@mfe/shared/schema-types';

import { ErrorsObj, RuntimeError } from '../graphql';
import { ProductInstanceTypes } from '../../../../platform/src/server/dataSources/PSM/types';

export type TokenInfo = {
  accessToken: string;
  accessTokenExpirationTime: string;
  refreshToken?: string;
  type?: TokenType;
  idToken?: string;
  locale?: Locale;
};

type AddressState = {
  address?: Address | null;
  coordinates?: Coordinates | null;
};

export type AuthUserState = {
  loading: boolean;
  error: ErrorsObj | RuntimeError | null;
  location: AddressState;
  user: {
    accountNumber: string;
    productInstanceId: string | null;
    productTypeId: string | null;
    productInstanceStatus: ProductInstanceStatus | null;
    productKind: string | null;
    isSoftSuspended: boolean;
    hasErrorProductInstance: boolean;
    hasCaf: boolean;
    relnId: string;
    partyId: string;
    indoorUnit: IndoorUnit | null;
    isFiberPreconnected: boolean;
    fiberCityId: string | null;
    fiberAddressId: string | null;
    serviceContractId: string | null;
    auth: {
      tokenInfo: TokenInfo;
      isAuthenticated: boolean;
      isAuthMFELoaded: boolean;
      username: string;
      subject?: string | null;
    };
  };
};

export const initialAuthUserState: AuthUserState = {
  loading: true,
  error: null,
  location: {
    address: undefined,
    coordinates: undefined,
  },
  user: {
    accountNumber: '',
    productInstanceId: null,
    productTypeId: null,
    productInstanceStatus: null,
    productKind: null,
    isSoftSuspended: false,
    hasErrorProductInstance: false,
    hasCaf: false,
    partyId: '',
    indoorUnit: null,
    fiberCityId: null,
    fiberAddressId: null,
    serviceContractId: null,
    isFiberPreconnected: false,
    relnId: '',
    auth: {
      tokenInfo: {
        accessToken: '',
        accessTokenExpirationTime: '',
        locale: Locale.EnUs,
      },
      username: '',
      isAuthenticated: false,
      isAuthMFELoaded: false,
    },
  },
};

export type SendAuthParamsAction = PayloadAction<{
  env: MvEnv;
  platform: Platform;
  mfeEnv: MvEnv;
  locale: Locale;
}>;

export const authUserSlice = createSlice({
  name: 'user',
  initialState: initialAuthUserState,
  reducers: {
    authUser: (state) => state,
    sendAuthParams: (state, { payload }: SendAuthParamsAction) => state,
    loginFromCode: (state, { payload }: { payload: { code: string; locale?: Locale } }) => state,
    loginFromUrl: (state) => state,
    loginFromCookie: (state, _: { payload: { stringifiedTokenInfo: string } }) => state,
    startRefreshCountdown: (state, _: { payload: { stringifiedTokenInfo: string } }) => state,
    setTokenInfo: (state, { payload }: { payload: TokenInfo }) => {
      state.loading = false;
      state.user.auth.tokenInfo = { ...state.user.auth.tokenInfo, ...payload };
    },
    setAuth: (state, { payload }: { payload: Partial<AuthUserState['user']['auth']> }) => {
      state.loading = false;
      state.user.auth = { ...state.user.auth, ...payload };
    },
    setRegistrationAuth: (state, { payload }: { payload: Partial<AuthUserState['user']['auth']> }) => {
      state.loading = false;
      state.user.auth = { ...state.user.auth, ...payload };
    },
    setAuthMFELoaded: (state, { payload }: { payload: { isAuthMFELoaded: boolean } }) => {
      state.user.auth.isAuthMFELoaded = payload.isAuthMFELoaded ?? false;
    },
    setUser: (state, { payload }: { payload: Partial<AuthUserState['user']> }) => {
      state.loading = false;
      state.user = { ...state.user, ...payload };
      state.error = null;
    },
    setLocation: (state, { payload }: { payload: AddressState }) => {
      state.location = payload;
    },
    setError: (state, { payload }: { payload: ErrorsObj | RuntimeError | null }) => {
      state.loading = false;
      state.error = payload;
    },
    fetchUserAction: (state) => {
      state.loading = true;
    },
    resetAuthUser: () => initialAuthUserState,
  },
});

export const {
  authUser,
  setUser,
  setTokenInfo,
  resetAuthUser,
  loginFromUrl,
  setAuth,
  loginFromCookie,
  startRefreshCountdown,
  sendAuthParams,
  loginFromCode,
  setAuthMFELoaded,
  setRegistrationAuth,
  fetchUserAction,
  setError: setAuthUserError,
  setLocation,
} = authUserSlice.actions;

export const selectUser = (state: {
  user: AuthUserState;
}): {
  user: AuthUserState['user'];
  loading: AuthUserState['loading'];
  error: AuthUserState['error'];
} => {
  return {
    user: state.user.user,
    loading: state.user.loading,
    error: state.user.error,
  };
};

export const selectLocation = (state: { user: AuthUserState }): AddressState | undefined => {
  return state.user.location;
};

export const selectServiceRegion = (state: { user: AuthUserState }): string => {
  return state.user.location.address?.region ?? '';
};

export const selectIsPreinstall = (state: { user: AuthUserState }): boolean => {
  return state.user.user.productInstanceStatus === ProductInstanceStatus.Preinstall;
};

export const selectHasFiber = (state: { user: AuthUserState }) =>
  state.user.user.productKind === ProductInstanceTypes.Fiber;

export const selectHasWirelessInternet = (state: { user: AuthUserState }) =>
  state.user.user.productKind === ProductInstanceTypes.WirelessInternet;
